'use client';

import Image from 'next/image';
import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { addItem } from './actions';
import { fetchProductRecommendations, RecommendedProductData } from './recommendation-actions';

// Types
interface RecommendedProduct extends RecommendedProductData {
  id: string;
}

interface YouMayAlsoLikeProps {
  maxItems?: number;
  cartItems?: Array<{
    merchandise: {
      product: {
        title: string;
        handle: string;
        id: string;
        variants?: any[];
        featuredImage?: {
          url: string;
          altText?: string;
        };
      };
    };
  }>;
  onUserInteraction?: () => void;
}

interface RecommendedItemProps extends RecommendedProduct {
  onAddToCart: (variantId: string) => Promise<void>;
  isLoading?: boolean;
}

// Helper function to get primary cart product handle for recommendations
const getPrimaryCartProductHandle = (cartItems: YouMayAlsoLikeProps['cartItems']): string | null => {
  if (!cartItems?.length) return null;
  
  // Sort by handle to get consistent primary product
  const sortedItems = [...cartItems].sort((a, b) => 
    a.merchandise.product.handle.localeCompare(b.merchandise.product.handle)
  );
  
  return sortedItems[0]?.merchandise.product.handle || null;
};

// Individual recommended item component
const RecommendedItem = ({ 
  id,
  variantId,
  handle,
  image, 
  title, 
  description, 
  price, 
  comparePrice, 
  availableForSale,
  onAddToCart,
  isLoading = false
}: RecommendedItemProps) => {
  const handleAddToCart = useCallback(async () => {
    if (!variantId || !availableForSale) {
      alert('This product is currently unavailable.');
      return;
    }
    
    if (!isLoading) {
      await onAddToCart(variantId);
    }
  }, [variantId, availableForSale, onAddToCart, isLoading]);

  return (
    <div className="flex w-full bg-[#F8F9FA] p-3 sm:p-4 rounded-lg">
      <div className="flex items-center gap-3 sm:gap-4 flex-1">
        {/* Product Image */}
        <div className="relative h-16 w-16 sm:h-20 sm:w-20 flex-shrink-0 overflow-hidden rounded-md">
          <Image
            src={image}
            alt={title}
            width={80}
            height={80}
            className="h-full w-full object-cover mix-blend-multiply"
          />
        </div>
        
        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-futura-book text-sm sm:text-base font-normal text-[#212529] line-clamp-2">
            {title}
          </h3>
          <p className="font-futura-book text-xs sm:text-sm font-normal text-[#73777F] line-clamp-1 mt-1">
            {description}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="font-futura-medium text-sm sm:text-base font-normal text-black">
              {price}
            </span>
            {comparePrice && (
              <span className="font-futura-medium text-xs sm:text-sm font-normal text-[#CCD2D9] line-through">
                {comparePrice}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Add to Cart Button */}
      <div className="flex items-center ml-3">
        <button
          onClick={handleAddToCart}
          disabled={isLoading || !availableForSale}
          className={`flex h-8 w-20 sm:h-10 sm:w-24 items-center justify-center rounded border border-[#CCD2D9] bg-white px-2 py-1 transition-colors ${
            isLoading || !availableForSale
              ? 'cursor-not-allowed opacity-50'
              : 'hover:bg-gray-50 active:bg-gray-100'
          }`}
        >
          {isLoading ? (
            <div className="h-3 w-3 animate-spin rounded-full border-b border-gray-400"></div>
          ) : (
            <span className="font-futura-medium text-xs sm:text-sm font-normal text-[#212529] truncate">
              {!availableForSale ? 'Unavailable' : 'Add to cart'}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

// Main "You May Also Like" component
export default function YouMayAlsoLike({ 
  maxItems = 3,
  cartItems = [],
  onUserInteraction
}: YouMayAlsoLikeProps) {
  const [isPending, startTransition] = useTransition();
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());
  const [recommendations, setRecommendations] = useState<RecommendedProduct[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  
  // Use refs to track previous values and prevent unnecessary re-fetches
  const previousPrimaryProductRef = useRef<string | null>(null);
  const previousCartItemCountRef = useRef<number>(0);
  const recommendationsCacheRef = useRef<RecommendedProduct[]>([]);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch recommendations with debouncing and caching
  useEffect(() => {
    const primaryProductHandle = getPrimaryCartProductHandle(cartItems);
    const currentCartItemCount = cartItems.length;
    
    // Only fetch if:
    // 1. This is the first load (isInitialLoading)
    // 2. The primary product changed
    // 3. The number of items in cart changed (items added/removed, not quantity changes)
    const shouldFetch = isInitialLoading || 
                       primaryProductHandle !== previousPrimaryProductRef.current ||
                       currentCartItemCount !== previousCartItemCountRef.current;
    
    if (!shouldFetch) {
      return;
    }
    
    // Clear any pending fetch
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // Debounce the fetch to prevent rapid re-fetches
    fetchTimeoutRef.current = setTimeout(async () => {
      try {
        const productRecommendations = await fetchProductRecommendations(cartItems);
        const slicedRecommendations = productRecommendations.slice(0, maxItems);
        
        // Only update if recommendations actually changed
        const hasChanged = JSON.stringify(slicedRecommendations) !== JSON.stringify(recommendationsCacheRef.current);
        
        if (hasChanged) {
          setRecommendations(slicedRecommendations);
          recommendationsCacheRef.current = slicedRecommendations;
        }
        
        // Update visibility after first successful fetch
        if (isInitialLoading && slicedRecommendations.length > 0) {
          setIsVisible(true);
        }
        
        setIsInitialLoading(false);
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        // Keep existing recommendations on error
        if (isInitialLoading) {
          setRecommendations([]);
          setIsInitialLoading(false);
        }
      }
    }, isInitialLoading ? 0 : 500); // No delay on initial load, 500ms delay on updates
    
    previousPrimaryProductRef.current = primaryProductHandle;
    previousCartItemCountRef.current = currentCartItemCount;
    
    // Cleanup
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [cartItems, maxItems, isInitialLoading]);

  const handleAddToCart = useCallback(async (variantId: string) => {
    const productItem = recommendations.find(p => p.variantId === variantId);
    if (!productItem) return;

    // Signal that this is a user interaction within the cart
    if (onUserInteraction) {
      onUserInteraction();
    }

    // Set loading state for this specific item
    setLoadingItems(prev => new Set([...prev, productItem.id]));

    startTransition(async () => {
      try {
        // Use the existing addItem server action
        const result = await addItem(null, variantId, 1);
        
        if (result.error) {
          // Show error message if there was an issue
          console.error('Error adding item to cart:', result.error);
          alert(`Failed to add item to cart: ${result.error}`);
        } else {
          // Success - the cart will update automatically due to revalidation
        }
      } catch (error) {
        console.error('Error adding recommended product to cart:', error);
        alert('Failed to add item to cart. Please try again.');
      } finally {
        // Remove loading state for this item
        setLoadingItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(productItem.id);
          return newSet;
        });
      }
    });
  }, [recommendations, onUserInteraction]);

  // Show loading skeleton only on initial load
  if (isInitialLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <h3 className="text-sm sm:text-base font-medium text-[#212529] mb-3 sm:mb-4 font-futura-book">
          You May Also Like
        </h3>
        <div className="space-y-3 sm:space-y-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex w-full bg-[#F8F9FA] p-3 sm:p-4 rounded-lg animate-pulse">
              <div className="flex items-center gap-3 sm:gap-4 flex-1">
                <div className="h-16 w-16 sm:h-20 sm:w-20 bg-gray-200 rounded-md"></div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                </div>
              </div>
              <div className="flex items-center ml-3">
                <div className="h-8 w-20 sm:h-10 sm:w-24 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Don't render if no recommendations, but keep the component mounted
  if (!recommendations || recommendations.length === 0 || !isVisible) {
    return null;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      <h3 className="text-sm sm:text-base font-medium text-[#212529] mb-3 sm:mb-4 font-futura-book">
        You May Also Like
      </h3>

      <div className="space-y-3 sm:space-y-4">
        {recommendations.map((product) => (
          <RecommendedItem
            key={product.id}
            {...product}
            onAddToCart={handleAddToCart}
            isLoading={loadingItems.has(product.id)}
          />
        ))}
      </div>
    </div>
  );
} 